import { useEffect, useRef, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import { DOC_TYPE_COLORS } from './IndividualDotLayer';

const getMarkerColor = (result, colorMode, categoryColorMap) => {
  if (colorMode === 'doctype') {
    return DOC_TYPE_COLORS[result.article_type] || '#6b7280';
  }
  const cat = result.categories?.[0];
  return (cat && categoryColorMap[cat]) || '#6b7280';
};

const createFractalClusterIcon = (cluster, colorMode, categoryColorMap) => {
  const count = cluster.getChildCount();
  const children = cluster.getAllChildMarkers();

  // Count category colors among children for pie-chart cluster icon
  const colorCounts = {};
  children.forEach(m => {
    const rd = m.options.resultData;
    if (!rd) return;
    const c = getMarkerColor(rd, colorMode, categoryColorMap);
    colorCounts[c] = (colorCounts[c] || 0) + 1;
  });
  const colors = Object.entries(colorCounts).sort((a, b) => b[1] - a[1]);
  const dominantColor = colors[0]?.[0] || '#FDE047';

  const baseSize = count < 10 ? 36 : count < 30 ? 44 : count < 80 ? 52 : count < 200 ? 60 : 68;
  const cx = baseSize / 2;
  const cy = baseSize / 2;
  const outerR = baseSize / 2 - 1;
  const innerR = outerR - 6;

  // Build pie segments from category distribution
  let arcs = '';
  let startAngle = -Math.PI / 2;
  const total = children.length || 1;
  colors.forEach(([color, cnt]) => {
    const sweep = (cnt / total) * 2 * Math.PI;
    const endAngle = startAngle + sweep;
    const x1 = cx + outerR * Math.cos(startAngle);
    const y1 = cy + outerR * Math.sin(startAngle);
    const x2 = cx + outerR * Math.cos(endAngle);
    const y2 = cy + outerR * Math.sin(endAngle);
    const largeArc = sweep > Math.PI ? 1 : 0;
    if (colors.length === 1) {
      arcs += `<circle cx="${cx}" cy="${cy}" r="${outerR}" fill="${color}" opacity="0.55"/>`;
    } else {
      arcs += `<path d="M${cx},${cy} L${x1},${y1} A${outerR},${outerR} 0 ${largeArc} 1 ${x2},${y2} Z" fill="${color}" opacity="0.55"/>`;
    }
    startAngle = endAngle;
  });

  const fs = baseSize < 44 ? 10 : baseSize < 52 ? 12 : 14;
  const svg = `<svg width="${baseSize}" height="${baseSize}" viewBox="0 0 ${baseSize} ${baseSize}" xmlns="http://www.w3.org/2000/svg">
    <circle cx="${cx}" cy="${cy}" r="${outerR}" fill="rgba(253, 251, 247,0.5)" stroke="${dominantColor}" stroke-width="1.5"/>
    ${arcs}
    <circle cx="${cx}" cy="${cy}" r="${innerR}" fill="rgba(253, 251, 247,0.92)" stroke="${dominantColor}" stroke-width="1"/>
    <text x="${cx}" y="${cy}" text-anchor="middle" dy="0.35em" fill="white" font-size="${fs}" font-weight="800" font-family="system-ui">${count}</text>
  </svg>`;

  return L.divIcon({
    html: `<div style="filter:drop-shadow(0 3px 10px ${dominantColor}40);cursor:pointer;transition:transform 0.2s;" 
                onmouseover="this.style.transform='scale(1.12)'" 
                onmouseout="this.style.transform='scale(1)'">${svg}</div>`,
    className: '',
    iconSize: L.point(baseSize, baseSize),
    iconAnchor: L.point(baseSize / 2, baseSize / 2),
  });
};

const createFractalMarkerIcon = (result, colorMode, categoryColorMap) => {
  const color = getMarkerColor(result, colorMode, categoryColorMap);
  return L.divIcon({
    className: '',
    html: `<div style="width:14px;height:14px;background:${color};border:2px solid rgba(255,255,255,0.9);border-radius:50%;box-shadow:0 0 8px ${color}88,0 1px 4px rgba(0,0,0,0.4);cursor:pointer;transition:transform 0.15s;"
                onmouseover="this.style.transform='scale(1.3)'" 
                onmouseout="this.style.transform='scale(1)'"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -8],
  });
};

const buildPopupHtml = (result, colorMode, categoryColorMap) => {
  const cat = result.categories?.[0] || 'Uncategorized';
  const dt = result.article_type || 'Unknown';
  const title = (result.title || result.url || 'Untitled').substring(0, 80);
  const snippet = (result.snippet || result.description || '').substring(0, 180);
  const catColor = categoryColorMap[cat] || '#6b7280';
  const dtColor = DOC_TYPE_COLORS[dt] || '#6b7280';
  const url = result.url || '#';
  const domain = result.root_domain || '';

  return `<div style="padding:8px 10px;max-width:340px;">
    <a href="${url}" target="_blank" rel="noopener" 
       style="color:#e5e7eb;font-size:0.8rem;font-weight:700;text-decoration:none;display:block;line-height:1.3;"
       title="${(result.title || '').replace(/"/g, '&quot;')}">${title}</a>
    ${domain ? `<div style="color:#60a5fa;font-size:0.65rem;margin-top:2px;">${domain}</div>` : ''}
    ${snippet ? `<div style="color:#a1a1aa;font-size:0.72rem;margin-top:5px;line-height:1.4;overflow:hidden;text-overflow:ellipsis;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;">${snippet}</div>` : ''}
    <div style="display:flex;gap:5px;margin-top:6px;flex-wrap:wrap;">
      <span style="padding:2px 7px;border-radius:4px;font-size:0.62rem;background:${catColor}22;color:${catColor};border:1px solid ${catColor}44;">${cat.substring(0, 30)}</span>
      <span style="padding:2px 7px;border-radius:4px;font-size:0.62rem;background:${dtColor}22;color:${dtColor};border:1px solid ${dtColor}44;">${dt}</span>
      ${(result.categories || []).slice(1, 3).map(c => {
        const cc = categoryColorMap[c] || '#6b7280';
        return `<span style="padding:2px 7px;border-radius:4px;font-size:0.62rem;background:${cc}22;color:${cc};border:1px solid ${cc}44;">${c.substring(0, 20)}</span>`;
      }).join('')}
    </div>
  </div>`;
};

// Offset overlapping dots at same coordinates
const applyOffset = (results) => {
  const seen = {};
  return results.map(r => {
    if (!r.latitude || !r.longitude) return r;
    const key = `${parseFloat(r.latitude).toFixed(5)}_${parseFloat(r.longitude).toFixed(5)}`;
    seen[key] = (seen[key] || 0) + 1;
    if (seen[key] > 1) {
      const angle = (seen[key] * 137.5) * (Math.PI / 180); // golden angle spread
      const dist = 0.0003 * Math.sqrt(seen[key]); // increase spread with count
      return {
        ...r,
        latitude: parseFloat(r.latitude) + dist * Math.cos(angle),
        longitude: parseFloat(r.longitude) + dist * Math.sin(angle)
      };
    }
    return r;
  });
};

const FractalClusterLayer = ({
  results,
  colorMode = 'category',
  categoryColorMap = {},
  onMarkerClick,
  onMarkerHover,
  onMarkerLeave
}) => {
  const map = useMap();
  const layersRef = useRef([]);

  const buildFractalLayers = useCallback(() => {
    layersRef.current.forEach((layer) => map.removeLayer(layer));
    layersRef.current = [];

    if (!results || results.length === 0) return;

    const offsetResults = applyOffset(results);

    const topGroup = L.markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: 80,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      animate: true,
      animateAddingMarkers: false,
      disableClusteringAtZoom: 16,
      iconCreateFunction: (cluster) => createFractalClusterIcon(cluster, colorMode, categoryColorMap),
    });

    offsetResults.forEach((result) => {
      if (!result.latitude || !result.longitude) return;
      const marker = L.marker([result.latitude, result.longitude], {
        icon: createFractalMarkerIcon(result, colorMode, categoryColorMap),
        resultData: result,
      });

      marker.bindPopup(() => buildPopupHtml(result, colorMode, categoryColorMap), {
        maxWidth: 360,
        className: 'fractal-dot-popup'
      });

      marker.on('click', () => onMarkerClick?.(result));
      if (onMarkerHover) marker.on('mouseover', (e) => { marker.openPopup(); onMarkerHover(result, e); });
      if (onMarkerLeave) marker.on('mouseout', () => { onMarkerLeave(); });

      topGroup.addLayer(marker);
    });

    const onZoom = () => topGroup.refreshClusters();
    map.on('zoomend', onZoom);
    topGroup.addTo(map);
    layersRef.current.push(topGroup);

    return () => map.off('zoomend', onZoom);
  }, [results, colorMode, categoryColorMap, map, onMarkerClick, onMarkerHover, onMarkerLeave]);

  useEffect(() => {
    const cleanup = buildFractalLayers();
    return () => {
      cleanup?.();
      layersRef.current.forEach((layer) => map.removeLayer(layer));
      layersRef.current = [];
    };
  }, [buildFractalLayers]);

  return null;
};

export default FractalClusterLayer;
